import * as React from 'react';
import BPromise from 'bluebird';
import Lazy from 'lazy.js';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, DEFAULT_LIMIT, DEFAULT_SKIP, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { getEmailOrMisEmail, getPhoneOrMisPhone, getUserRole } from 'Src/helpers/accessor/accessor';
import { SchoolUser } from 'Src/models/schoolUser';
import {
  deleteDataToPermission,
  getAllSchoolStaffs,
  getSchoolStaffs,
  getSchoolStaffsCount
} from 'Src/helpers/service/admin/staffs';
import { STAFF_LIST } from 'Src/consts/user';
import { ResizablePanel } from 'Src/components/ResizablePanel/ResizablePanel';
import { StaffSummary } from 'Src/components/StaffSummary/StaffSummary';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { RoleRemoveModal } from 'Src/components/StaffListModals/RoleRemoveModal';
import { MEMBER_OF_STAFF_TABS } from 'Src/consts/school';
import { GenericSummary } from 'Src/components/GenericSummary/GenericSummary';
import propz from 'propz';
import { countActivePeopleFilters, PeopleFilterChips } from '../../AdminView/Users/PeopleFilterChips';
import '../../AdminView/Users/Students/Students.scss';
import '../../AdminView/Users/Staffs/Staffs.css';
import { ListEmptyState } from 'Src/components/ListEmptyState/ListEmptyState';
import { faUsersCog } from '@fortawesome/free-solid-svg-icons';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'firstName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['firstName']
  },
  {
    text: 'Surname',
    field: 'lastName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['lastName']
  },
  {
    text: 'Email',
    field: 'email',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: getEmailOrMisEmail
  },
  {
    text: 'Phone',
    field: 'phone',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: getPhoneOrMisPhone
  },
  {
    text: 'Role',
    field: 'role',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getUserRole
  }
];

const FILTER_COLUMNS: ColumnDefinition[] = [
  ...COLUMNS,
  { text: 'Name', field: 'nameSearch', isSort: false, type: FILTER_TYPE.TEXT, accessor: ['firstName'] }
];

interface State {
  items: SchoolUser[];
  currentPage: number;
  selectedItems: SchoolUser[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  basePath: string;
  currentTabIndex: number;
  editablePermissionId: string;
  isAddRoleModalOpen: boolean;
  isListActionsModalOpen: boolean;
  activeListAction: STAFF_LIST_ACTIONS;
  showConfirmationDialog: boolean;
  isRoleRemoveModalOpen: boolean;
  roleToRemove: string;
  previewStaff: SchoolUser | null;
  hiddenStaffColumnKeys: string[];
  isColumnsMenuOpen: boolean;
  isSelectionMenuOpen: boolean;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

enum STAFF_LIST_ACTIONS {
  REMOVE_ROLE = 'REMOVE_ROLE'
}

export class StaffsSU extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      basePath: '',
      currentTabIndex: 0,
      editablePermissionId: '',
      isAddRoleModalOpen: false,
      isListActionsModalOpen: false,
      activeListAction: null,
      showConfirmationDialog: false,
      isRoleRemoveModalOpen: false,
      roleToRemove: '',
      previewStaff: null,
      hiddenStaffColumnKeys: this.getInitialHiddenStaffColumnKeys(),
      isColumnsMenuOpen: false,
      isSelectionMenuOpen: false
    };
  }

  getStaffColumnsStorageKey = (): string => `sit.unionAdmins.columns.${this.props.user.userId}`;

  getInitialHiddenStaffColumnKeys = (): string[] => {
    try {
      return JSON.parse(window.localStorage.getItem(this.getStaffColumnsStorageKey()) || '[]');
    } catch (_error) {
      return [];
    }
  };

  persistHiddenStaffColumnKeys = (hiddenStaffColumnKeys: string[]): void => {
    window.localStorage.setItem(this.getStaffColumnsStorageKey(), JSON.stringify(hiddenStaffColumnKeys));
    this.setState({ hiddenStaffColumnKeys });
  };

  toggleStaffColumn = (field: string): void => {
    const hidden = new Set(this.state.hiddenStaffColumnKeys);
    hidden.has(field) ? hidden.delete(field) : hidden.add(field);
    this.persistHiddenStaffColumnKeys(Array.from(hidden));
  };

  onDocumentClick = (event: MouseEvent): void => {
    if (!this.state.isColumnsMenuOpen) return;
    const target = event.target as HTMLElement | null;
    if (target && target.closest && target.closest('.people-columns')) return;
    this.setState({ isColumnsMenuOpen: false });
  };

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    document.addEventListener('click', this.onDocumentClick);

    this.setAdditionalItems().then(res => {
      this.setItems();
    });
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setItems(nextProps);
    }
  }

  onModernSearchChange = (field: string, value: string): void => {
    const filters = { ...this.state.filters };
    if (value === '') delete filters[field];
    else filters[field] = value;
    this.setState({ filters });
  };

  setAdditionalItems() {
    const { user } = this.props;

    return BPromise.all([]).then(([]) => {
      this.setState({});

      return true;
    });
  }

  setItems(props = this.props) {
    const { history, user, location } = props;

    const search = parse(location.search || '');
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(FILTER_COLUMNS, search);

    const isShowFilter = false;
    const isDataFiltered = isFilterExist2(filters);

    const where = this.getStaffWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const getItemsPromise = getSchoolStaffs(user, serverQueryFilter);
    const getItemsCountPromise = getSchoolStaffsCount(user, where);

    const promises = [getItemsCountPromise, getItemsPromise];

    return BPromise.all(promises).then(([countObj, items]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: isShowFilter,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: items,
        total: countObj.count,
        basePath: location.pathname,
        isLoading: false
      });

      return true;
    });
  }

  getStaffWhere = (filters: any): any => {
    const { nameSearch, ...fieldFilters } = filters || {};
    const where = getServerFieldSectionWhere(fieldFilters);
    const term = String(nameSearch || '').trim();
    if (term) {
      where.$or = [
        { firstName: { like: term, options: 'i' } },
        { lastName: { like: term, options: 'i' } }
      ];
    }
    return where;
  };

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = this.getStaffWhere(filters);

    getAllSchoolStaffs(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onCancelFilterClick = (): void => {
    const filters = getFilters2(FILTER_COLUMNS, parse(this.props.location.search || ''));
    this.setState({ filters, isShowFilter: false });
  };

  onClearFilterDraftClick = (): void => {
    this.setState({ filters: {} });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    if (event && event.preventDefault) event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  getSelectedUsersIdWithStaffPermission(): { userId: string; permission: any }[] {
    const { selectedItems } = this.state;
    let idsList: { userId: string; permission: any }[] = [];

    [...selectedItems].forEach(staff => {
      const permission = staff.permissions.find(perm => STAFF_LIST.findIndex(preset => preset === perm.preset) !== -1);

      idsList.push({ userId: staff.id, permission: permission });
    });

    return idsList;
  }

  onCloseActionsPopupClick = (): void => {
    this.setState({
      isListActionsModalOpen: false,
      activeListAction: null
    });
  };

  getRolesFiltered() {
    const { selectedItems } = this.state;

    const permissions = Lazy(selectedItems)
      .map(item => item.permissions)
      .flatten()
      .toArray();

    const roles = permissions.map(permission => permission.preset);

    return roles.filter((role, index, self) => self.indexOf(role) === index);
  }

  onRemoveRoleClick = (role: string): void => {
    this.setState({
      isRoleRemoveModalOpen: true,
      roleToRemove: role
    });
  };

  handleRoleRemoveConfirm = (): void => {
    const { selectedItems } = this.state;
    const { user } = this.props;

    const promises = selectedItems.map(item => {
      const userId = item.id;
      const permissions = item.permissions.filter(
        permission => permission.schoolId === user.activeSchoolId && STAFF_LIST.includes(permission.preset)
      );
      return Promise.all(permissions.map(permission => deleteDataToPermission(user, userId, permission.id)));
    });

    this.setState({ isLoading: true });

    Promise.all(promises).then(() => {
      this.setState({
        isLoading: false,
        isRoleRemoveModalOpen: false,
        roleToRemove: '',
        selectedItems: []
      });
      this.setItems();
    });
  };

  handleRoleRemoveCancel = (): void => {
    this.setState({
      isRoleRemoveModalOpen: false,
      roleToRemove: ''
    });
  };

  renderRoleRemoveModal(): React.ReactNode {
    const { isRoleRemoveModalOpen, selectedItems } = this.state;

    if (!isRoleRemoveModalOpen) {
      return null;
    }

    return (
      <SimpleModal
        isOpen={isRoleRemoveModalOpen}
        title="Revoke union admin role?"
        customClass="peopleDangerModal"
        dialogStyle={{ maxWidth: 500 }}
        zIndex={2147483100}
        showFooter={false}
        isBodyBold={false}
        onCloseClick={this.handleRoleRemoveCancel}
      >
        <div className="people-delete people-roleRevoke">
          <div className="people-delete__alert people-delete__alert--noIcon">
            <div>
              <h3>Union access will be removed immediately.</h3>
              <p>
                {selectedItems.length} {selectedItems.length === 1 ? 'administrator' : 'administrators'} will lose their
                only role in this union.
              </p>
            </div>
          </div>
          <div className="people-delete__details">
            <p>They must request union access again before an administrator can restore it.</p>
            <p className="people-delete__names">
              Selected:{' '}
              {selectedItems
                .map(admin => `${admin.firstName || ''} ${admin.lastName || ''}`.trim() || admin.email)
                .join(', ')}
            </p>
          </div>
          <div className="people-delete__footer">
            <button type="button" className="sit-btn sit-btn--ghost" onClick={this.handleRoleRemoveCancel}>
              Cancel
            </button>
            <button type="button" className="sit-btn sit-btn--danger" onClick={this.handleRoleRemoveConfirm}>
              Revoke role
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  }

  renderActionsModal(): React.ReactNode {
    const { user } = this.props;
    const { isListActionsModalOpen, activeListAction, selectedItems } = this.state;

    if (!isListActionsModalOpen) {
      return null;
    }

    switch (activeListAction) {
      case STAFF_LIST_ACTIONS.REMOVE_ROLE:
        const roles = this.getRolesFiltered();
        if (selectedItems.length === 1) {
          return (
            <RoleRemoveModal
              user={user}
              roles={roles}
              onSubmitClick={this.onRemoveRoleConfirmed}
              onCancelClick={this.onCloseActionsPopupClick}
              isOpen={isListActionsModalOpen}
            />
          );
        }
        break;
      default:
        return null;
    }
  }

  renderStaffTable(): React.ReactNode {
    const s = this.state;
    const hidden = new Set(s.hiddenStaffColumnKeys);
    const locked = new Set(['firstName']);
    const visibleColumns = COLUMNS.filter(column => locked.has(column.field) || !hidden.has(column.field));
    const selectedIds = new Set(s.selectedItems.map(item => item.id));
    const allPageSelected = s.items.length > 0 && s.items.every(item => selectedIds.has(item.id));
    const setFilter = (field: string, value: any) =>
      this.setState({ filters: { ...this.state.filters, [field]: value } });
    const toggleAdmin = (admin: SchoolUser) => {
      const index = s.items.findIndex(item => item.id === admin.id);
      if (index !== -1) this.onItemClick(index);
    };
    const fullName = (admin: SchoolUser) => `${admin.firstName || ''} ${admin.lastName || ''}`.trim();
    const initials = (admin: SchoolUser) => `${(admin.firstName || '?')[0]}${(admin.lastName || '')[0] || ''}`;
    const renderAvatar = (admin: SchoolUser) => {
      const avatar = propz.get(admin, ['avatar'], '');
      return avatar ? (
        <span className="people-avatar staff-avatar--image" style={{ backgroundImage: `url(${avatar})` }} />
      ) : (
        <span className="people-avatar">{initials(admin)}</span>
      );
    };
    const cell = (admin: SchoolUser, field: string) => {
      switch (field) {
        case 'firstName':
          return (
            <span className="staff-nameCell">
              {renderAvatar(admin)}
              <strong>{fullName(admin) || 'Unnamed user'}</strong>
            </span>
          );
        case 'lastName':
          return admin.lastName || '—';
        case 'email':
          return getEmailOrMisEmail(admin) || '—';
        case 'phone':
          return getPhoneOrMisPhone(admin) || '—';
        case 'role':
          return (
            <span className="staff-modernChips">
              {(admin.permissions || [])
                .filter(p => p.schoolId === this.props.user.activeSchoolId && STAFF_LIST.includes(p.preset))
                .map(p => (
                  <span key={p.id} className="staff-modernChip staff-modernChip--role">
                    {p.preset}
                  </span>
                ))}
            </span>
          );
        default:
          return '—';
      }
    };

    return (
      <div className="people-screen staff-modernScreen">
        <div className="unionAdminsListCard">
        <div className="people-toolbar">
          <div className="people-toolbar__heading">
            <label className="unionAdminsSearch">
              <input
                value={s.filters.nameSearch || ''}
                placeholder="Search by name or surname…"
                onChange={event => this.onModernSearchChange('nameSearch', event.target.value)}
                onKeyDown={event => event.key === 'Enter' && this.onApplyFilterClick()}
              />
            </label>
            <button
              type="button"
              className={`people-btn people-btn--secondary people-btn--iconLeft ${
                s.isShowFilter ? 'people-btn--on' : ''
              }`}
              onClick={this.onTableFilterClick}
            >
              <span className="people-icon people-icon--filter" /> Filters
              {countActivePeopleFilters(s.filters) > 0 && (
                <span className="people-btn__badge">{countActivePeopleFilters(s.filters)}</span>
              )}
            </button>
            <div className="people-columns">
              <button
                type="button"
                className="people-btn people-btn--secondary people-btn--iconLeft"
                onClick={() => this.setState({ isColumnsMenuOpen: !s.isColumnsMenuOpen })}
              >
                <span className="people-icon people-icon--columns" /> Columns{' '}
                <span className="people-btn__badge">{visibleColumns.length}</span>
              </button>
              {s.isColumnsMenuOpen && (
                <div className="people-columns__menu">
                  <div className="people-columns__head">
                    <span>Show / hide columns</span>
                    <button type="button" onClick={() => this.persistHiddenStaffColumnKeys([])}>
                      Reset
                    </button>
                  </div>
                  {COLUMNS.map(column => (
                    <label
                      key={column.field}
                      className={`people-columns__item ${
                        locked.has(column.field) ? 'people-columns__item--locked' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={locked.has(column.field) || !hidden.has(column.field)}
                        disabled={locked.has(column.field)}
                        onChange={() => this.toggleStaffColumn(column.field)}
                      />
                      <span>{column.text}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {s.isShowFilter && (
          <>
          <div className="unionAdminsFilterScrim" onClick={this.onCancelFilterClick} />
          <form
            className="people-filter staff-filter"
            onSubmit={event => {
              event.preventDefault();
              this.onApplyFilterClick();
            }}
          >
            <div className="people-filter__header">
              <strong>Filters</strong>
              <button type="button" onClick={this.onCancelFilterClick} aria-label="Close filters">
                ×
              </button>
            </div>
            <div className="people-filter__body">
              <div className="unionAdminsFilterFields">
                <label><span>Email</span><input value={s.filters.email || ''} onChange={event => setFilter('email', event.target.value)} placeholder="Search email…" /></label>
                <label><span>Phone</span><input value={s.filters.phone || ''} onChange={event => setFilter('phone', event.target.value)} placeholder="Search phone…" /></label>
              </div>
            </div>
            <div className="people-filter__actions unionAdminsFilterActions">
              <button type="button" className="people-btn people-btn--ghost" onClick={this.onClearFilterDraftClick}>
                Clear all
              </button>
              <div>
                <button type="button" className="people-btn people-btn--secondary" onClick={this.onCancelFilterClick}>Cancel</button>
                <button type="submit" className="people-btn people-btn--primary">Apply</button>
              </div>
            </div>
          </form>
          </>
        )}

        <PeopleFilterChips
          filters={s.filters}
          columns={FILTER_COLUMNS}
          valueLabels={{}}
          onRemove={field => this.setState({ filters: { ...s.filters, [field]: '' } }, this.onApplyFilterClick)}
          onClear={this.onClearFilterClick}
        />

        {s.selectedItems.length > 0 && (
          <div className="people-selection">
            <span className="people-selection__summary">{s.selectedItems.length} selected</span>
            <div className="people-selection__actions">
              <button
                type="button"
                className="people-btn people-btn--danger"
                disabled={s.selectedItems.some(item => item.id === this.props.user.userId)}
                onClick={() => this.setState({ isRoleRemoveModalOpen: true })}
              >
                Revoke role
              </button>
            </div>
            <div className="people-toolbar__spacer" />
            <button type="button" className="people-selection__clear" onClick={this.onUnselectAllClick}>
              Clear selection
            </button>
          </div>
        )}

        <div className="people-tableCard">
          <div className="people-tableScroll">
            <table className="people-table staff-modernTable">
              <thead>
                <tr>
                  <th className="people-table__check">
                    <SelectAllCheckbox
                      compact
                      isChecked={allPageSelected}
                      totalCount={s.total}
                      currentPage={s.currentPage}
                      pageItemsCount={s.items.length}
                      selectedItems={s.selectedItems}
                      onSelectAllOnPageClick={this.onSelectAllOnPageClick}
                      onSelectAllClick={this.onSelectAllClick}
                      onUnselectAllClick={this.onUnselectAllClick}
                    />
                  </th>
                  {visibleColumns.map(column => (
                    <th
                      key={column.field}
                      className={column.isSort ? 'people-table__sortable' : ''}
                      onClick={column.isSort ? () => this.onTableSortClick(column.field) : undefined}
                    >
                      <span>{column.text}</span>
                      {column.isSort && (
                        <i
                          className={`people-sort ${
                            s.sortColumnsName === column.field ? `people-sort--${s.sortDirection}` : ''
                          }`}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {s.items.length === 0 ? (
                  <tr>
                    <td className="people-empty" colSpan={visibleColumns.length + 1}>
                      <ListEmptyState
                        icon={faUsersCog}
                        title={s.isDataFiltered ? 'No union admins match your filters' : 'No union admins yet'}
                        description={
                          s.isDataFiltered
                            ? 'Try changing or clearing the search and filters to see more union admins.'
                            : 'Union administrators will appear here when access to this union is granted.'
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  s.items.map(admin => (
                    <tr
                      key={admin.id}
                      data-selected={selectedIds.has(admin.id)}
                      data-preview={s.previewStaff && s.previewStaff.id === admin.id}
                      onClick={() => this.setState({ previewStaff: admin })}
                    >
                      <td className="people-table__check" onClick={event => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(admin.id)}
                          onChange={() => toggleAdmin(admin)}
                        />
                      </td>
                      {visibleColumns.map(column => (
                        <td key={column.field}>{cell(admin, column.field)}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {s.total > 0 ? (
          <Pagination2 modern currentPage={s.currentPage} total={s.total} setCurrentPageParams={this.setCurrentPageParams} />
        ) : null}
        </div>
      </div>
    );
  }

  isOneItemSelected(): boolean {
    const selectedStaffs = this.state.selectedItems;
    return selectedStaffs.length === 1;
  }

  onListActionClick = (actionType: STAFF_LIST_ACTIONS): void => {
    if (actionType === STAFF_LIST_ACTIONS.REMOVE_ROLE) {
      this.setState({ showConfirmationDialog: true, activeListAction: actionType });
    }
  };

  onConfirmRemoveRole = (): void => {
    const { selectedItems } = this.state;

    if (selectedItems.length === 1) {
      const role = 'Admin';
      this.onRemoveRoleClick(role);
    }

    this.setState({ showConfirmationDialog: false });
  };

  onCancelRemoveRole = (): void => {
    this.setState({ showConfirmationDialog: false });
  };

  onRemoveRoleConfirmed = (role: string): void => {
    const { user } = this.props;
    const { selectedItems } = this.state;

    const promises = selectedItems.map(item => {
      const userId = item.id;
      const permissions = item.permissions.filter(permission => permission.preset === role);

      return BPromise.all(permissions.map(permission => deleteDataToPermission(user, userId, permission.id)));
    });

    this.setState({ isLoading: true });

    BPromise.all(promises)
      .then(() => {
        this.setState({
          isListActionsModalOpen: false,
          selectedItems: [],
          isSelectAllChecked: false
        });
        this.setItems();
      })
      .catch(error => {
        console.error('Error removing role:', error);
        this.setState({ isLoading: false });
      });
  };

  renderSummary(): React.ReactNode {
    const admin = this.state.previewStaff;
    if (!admin) return null;
    const name = `${admin.firstName || ''} ${admin.lastName || ''}`.trim() || 'Unnamed user';
    const roles = (admin.permissions || []).filter(
      permission => permission.schoolId === this.props.user.activeSchoolId && STAFF_LIST.includes(permission.preset)
    );
    const avatar = propz.get(admin, ['avatar'], '');
    const initials = `${(admin.firstName || '?')[0]}${(admin.lastName || '')[0] || ''}`;

    return (
      <>
        <div className="people-drawerScrim" onClick={() => this.setState({ previewStaff: null })} />
        <aside className="people-drawer" role="dialog" aria-modal="true">
          <div className="people-drawer__head">
            {avatar ? (
              <span
                className="people-avatar people-avatar--lg staff-avatar--image"
                style={{ backgroundImage: `url(${avatar})` }}
              />
            ) : (
              <span className="people-avatar people-avatar--lg">{initials}</span>
            )}
            <div className="people-drawer__identity">
              <h2>{name}</h2>
              <div>
                <span>{roles.map(role => role.preset).join(', ') || 'Union administrator'}</span>
                <i />
                <span>{getEmailOrMisEmail(admin) || 'No email'}</span>
              </div>
            </div>
            <button
              type="button"
              className="people-drawer__close"
              onClick={() => this.setState({ previewStaff: null })}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="people-drawer__tabs">
            <button type="button" className="active">
              Overview
            </button>
          </div>
          <div className="people-drawer__body">
            <StaffSummary staff={admin} />
          </div>
          <div className="people-drawer__foot staff-drawerFoot">
            <button
              type="button"
              className="sit-btn sit-btn--danger"
              disabled={admin.id === this.props.user.userId}
              onClick={() => this.setState({ selectedItems: [admin], isRoleRemoveModalOpen: true })}
            >
              Revoke role
            </button>
          </div>
        </aside>
      </>
    );
  }

  onSummaryTabClick = (event: any, index: number): void => {
    event.preventDefault();
    this.setState({
      currentTabIndex: index
    });
  };

  getStaffSummaryTabs(): React.ReactNode {
    const { selectedItems } = this.state;

    if (selectedItems.length === 1) {
      return <StaffSummary staff={selectedItems[0]} />;
    }

    return null;
  }

  render() {
    const { isLoading } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="mt-3 staffsPage">
        {this.renderRoleRemoveModal()}
        <header className="unionAdminsPageHeader">
          <h1>Union admins</h1>
          <span>{this.state.total}</span>
        </header>
        {this.renderStaffTable()}
        {this.renderSummary()}
      </div>
    );
  }
}
